import React, { useMemo, useState, useContext, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import classNames from 'classnames';
import SegmentedControl from '../../SegmentedControl';
import RightPanel from '../Dashboard/RightPanel';
import ApplicationCard from './ApplicationCard';
import CandidateDetails from '../CandidateDetails';
import UserService from '../../../services/UserService';
import classes from './styles.module.scss';
import { useUserStore } from '../../../store';
import AdminService from '../../../services/AdminService';
import StatsList from './StatsList';
import JobSummary from './JobSummary';
import { UiContext } from '../../../context/UiContext';
import CalendarService from '../../../services/CalendarService';
import { getEvents } from '../../../helpers/getEvents';
import InterviewCard from '../InterviewDashboard/InterviewCard';

export default function CompanyDashboard() {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState({ value: 'submited' });
  const [isCandidatePopupVisible, setIsCandidatePopupVisible] = useState(false);
  const [currentCandidate, setCurrentCandidate] = useState(null);
  const companyId = useUserStore((state) => state.companyId);
  const [currentEvent, setCurrentEvent] = useState(null);

  const { setIsFetching } = useContext(UiContext);

  const { data: user } = useQuery({
    queryKey: ['me'],
    queryFn: UserService.getMe,
  });

  const { data: calendarEvents, refetch: refetchCalendarEvents } = useQuery({
    queryKey: ['getInteviewerEvents'],
    queryFn: CalendarService.getEvents,
    keepPreviousData: true,
  });

  const allEvents = useMemo(() => {
    return getEvents(
      calendarEvents,
      user?.id,
      null,
      user?.userProfile?.timezone
    );
  }, [calendarEvents, user]);

  console.log('allEvents', allEvents);

  const upcoming = useMemo(
    () =>
      allEvents
        ?.filter(
          (e) =>
            e.type === 'Interview' &&
            [
              'Scheduled',
              'Accepted',
              'Rescheduled',
              'Pending',
              'ChangeRequest',
            ].includes(e.status) &&
            (e.eventDate == null || new Date(e.eventDate) >= new Date())
        )
        .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate)),
    [allEvents]
  );

  const pendingFeedback = useMemo(
    () =>
      allEvents
        ?.filter(
          (e) =>
            e.type === 'Interview' &&
            e.eventDate != null &&
            new Date(e.eventDate) < new Date() &&
            e.status !== 'Canceled' &&
            e.status !== 'Declined' &&
            e.status !== 'Requested' &&
            !e.event.jobApplicationInterviewFeedbacks?.some(
              (feedback) => feedback.userId === user.id
            )
        )
        .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate)),
    [allEvents, user]
  );

  // Sync currentEvent with refetched data
  useEffect(() => {
    if (currentEvent && allEvents) {
      const updatedEvent = allEvents.find(
        (event) => event?.id === currentEvent?.id
      );
      if (updatedEvent) {
        setCurrentEvent(updatedEvent);
        setCurrentCandidate(updatedEvent.originalEvent.jobApplication);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEvents, currentEvent?.id]);

  const { data: jobPages } = useQuery({
    queryKey: ['companyJobs', companyId],
    queryFn: () => {
      const filters = ['status:noteq:Draft'];
      filters.push(`company.id:eq:${companyId}`);
      filters.push(`jobPostType:eq:External`);
      return AdminService.getCompanyJobs({
        pageNumber: 1,
        pageSize: 9999,
        Filters: filters,
      });
    },
    enabled: !!companyId,
    keepPreviousData: true,
  });

  const jobs = jobPages?.data;

  const { data: statsData } = useQuery({
    queryKey: ['companyDashboardStats', companyId],
    queryFn: () =>
      AdminService.getCompanyDashboardStats({
        companyId,
      }),
    keepPreviousData: true,
  });

  const {
    data: jobApplications,
    refetch: refetchJobApplications,
    isFetching,
  } = useQuery({
    queryKey: ['companyJobApplications', companyId],
    queryFn: () => {
      return AdminService.getCompanyJobApplications({
        pageNumber: 1,
        pageSize: 9999,
        companyId,
        Filters: [`statusInt:gt:3`, `subStatus:noteq:Rejected`],
      });
    },
    enabled: !!companyId,
    keepPreviousData: false,
  });

  useEffect(() => {
    setIsFetching(isFetching);
  }, [isFetching, setIsFetching]);

  const submitted = useMemo(() => {
    const filtered = jobApplications?.data?.filter(
      (application) => application.statusInt === 4
    );
    if (!filtered?.length) return [];

    const sorted = [...filtered].sort((a, b) => {
      // Handle both possible data structures
      const statusChangedAtA =
        a.statusChangedAt || a.jobApplication?.statusChangedAt;
      const statusChangedAtB =
        b.statusChangedAt || b.jobApplication?.statusChangedAt;

      const dateA = statusChangedAtA ? new Date(statusChangedAtA).getTime() : 0;
      const dateB = statusChangedAtB ? new Date(statusChangedAtB).getTime() : 0;

      // Handle invalid dates
      if (Number.isNaN(dateA) && Number.isNaN(dateB)) return 0;
      if (Number.isNaN(dateA)) return 1; // Invalid dates go to end
      if (Number.isNaN(dateB)) return -1; // Valid dates before invalid

      return dateB - dateA; // Descending: newest first
    });

    return sorted;
  }, [jobApplications]);

  const segmentItems = useMemo(() => {
    return [
      {
        label: t(
          'dashboardComponents.CompanyDashboard.Picker.SubmittedCandidates'
        ),
        value: 'submited',
        count: submitted?.length || 0,
      },
      {
        label: t(
          'dashboardComponents.CompanyDashboard.Picker.UpcomingInterviews'
        ),
        value: 'upcoming',
        count: upcoming?.length || 0,
      },
      {
        label: t(
          'dashboardComponents.CompanyDashboard.Picker.CompletedInterviews'
        ),
        value: 'pendingFeedback',
        count: pendingFeedback?.length || 0,
      },
    ];
  }, [upcoming, pendingFeedback, submitted, t]);

  const showList = useMemo(() => {
    if (selectedTab?.value === 'upcoming') {
      return upcoming;
    }
    if (selectedTab?.value === 'pendingFeedback') {
      return pendingFeedback;
    }
    return submitted;
  }, [selectedTab?.value, upcoming, pendingFeedback, submitted]);

  return (
    <div className={classes.CompanyDashboard}>
      <div className={classes.leftCol}>
        <div className={classes.titleRow}>
          <h1>Dashboard</h1>
        </div>
        <div className={classes.statsRow}>
          <StatsList data={statsData} />
        </div>
        <div className={classes.row}>
          <div className={classes.col}>
            <div className={classes.listCol}>
              <div className={classes.listHeader}>
                <SegmentedControl
                  ariaLabel={t('pages.InterviewDashboard.segmentAria')}
                  items={segmentItems}
                  selectedItem={selectedTab}
                  onChange={(item) => setSelectedTab(item)}
                />
              </div>
              <div
                className={classNames(classes.listScroll, {
                  [classes.empty]: !showList?.length,
                })}
              >
                {!showList?.length ? (
                  <div className={classes.emptyText}>
                    {(() => {
                      if (selectedTab?.value === 'submited') {
                        return t(
                          'dashboardComponents.CompanyDashboard.emptyTextSubmitted'
                        );
                      }
                      if (selectedTab?.value === 'upcoming') {
                        return t('pages.InterviewDashboard.emptyText');
                      }
                      return t('pages.InterviewDashboard.emptyTextFeedback');
                    })()}
                  </div>
                ) : (
                  <div className={classes.cards}>
                    {(() => {
                      if (selectedTab?.value === 'submited') {
                        return showList?.map((application) => (
                          <div
                            key={`${application.id}-${application.status}`}
                            className={classes.cardWrap}
                          >
                            <ApplicationCard
                              data={application}
                              user={user}
                              refetch={refetchJobApplications}
                              onViewCandidate={() => {
                                setCurrentCandidate(application);
                                setIsCandidatePopupVisible(true);
                              }}
                            />
                          </div>
                        ));
                      }
                      if (selectedTab?.value === 'upcoming') {
                        return showList?.map((event) => (
                          <div
                            key={`${event.id}-${event.status}`}
                            className={classes.cardWrap}
                          >
                            <InterviewCard
                              event={event}
                              user={user}
                              refetch={refetchCalendarEvents}
                              onViewCandidate={() => {
                                setCurrentEvent(event);
                                setCurrentCandidate(
                                  event.originalEvent.jobApplication
                                );
                                setIsCandidatePopupVisible(true);
                              }}
                            />
                          </div>
                        ));
                      }
                      return showList?.map((event) => (
                        <div
                          key={`${event.id}-${event.status}`}
                          className={classes.cardWrap}
                        >
                          <InterviewCard
                            event={event}
                            user={user}
                            refetch={refetchCalendarEvents}
                            onViewCandidate={() => {
                              setCurrentEvent(
                                jobApplications?.data
                                  ?.find(
                                    (application) =>
                                      application.id ===
                                      event.originalEvent.jobApplication.id
                                  )
                                  ?.jobApplicationInterviews?.find(
                                    (interview) => interview.id === event.id
                                  )
                              );
                              setCurrentCandidate(
                                event.originalEvent.jobApplication
                              );
                              setIsCandidatePopupVisible(true);
                            }}
                          />
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={classNames(classes.col, classes.jobSummaryCol)}>
            <div className={classes.jobSummaryHeader}>
              <JobSummary stats={statsData} />
            </div>
            <div className={classes.jobSummaryScroll}>
              <div className={classes.OpenJobsSection}>
                <div className={classes.SectionHeader}>
                  <div className={classes.HeaderLeft}>
                    <h3 className={classes.SectionTitle}>
                      {t('details.openJobs', 'Open Jobs')}
                    </h3>
                    <span
                      className={classNames(
                        classes.CountBadge,
                        jobs?.length === 0 ? classes.empty : ''
                      )}
                    >
                      {jobs?.length ?? 0}
                    </span>
                  </div>
                </div>
                {jobs?.length > 0 ? (
                  <>
                    <div className={classes.TableHeader}>
                      <div className={classes.HeaderColumn}>
                        {t('details.jobTitle', 'Job Title')}
                      </div>
                      <div className={classes.HeaderColumn}>
                        {t('details.candidates', 'Submitted')}
                      </div>
                      <div className={classes.HeaderColumn}>
                        {t('details.daysOpen', 'Days Open')}
                      </div>
                    </div>

                    <div className={classes.JobsList}>
                      {jobs?.map((job) => (
                        <div key={job.id} className={classes.JobRow}>
                          <div className={classes.JobTitle}>{job.jobTitle}</div>
                          <div className={classes.CandidateCount}>
                            <span className={classes.DaysBadge}>
                              {job.summary.clientSubmit}
                            </span>
                          </div>
                          <div className={classes.DaysOpen}>
                            <span className={classes.DaysBadge}>
                              {job.daysOpen}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className={classes.EmptyText}>
                    There are no open jobs at the moment
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={classes.rightCol}>
        <RightPanel />
      </div>

      <CandidateDetails
        candidate={
          currentCandidate?.user || currentCandidate?.jobApplication?.user
        }
        jobApplicationId={
          currentCandidate?.id || currentCandidate?.jobApplication?.id
        }
        isVisible={isCandidatePopupVisible}
        event={currentEvent}
        setIsVisible={() => {
          setIsCandidatePopupVisible();
          setCurrentCandidate(null);
          setCurrentEvent(null);
        }}
        refetch={async () => {
          await refetchCalendarEvents();
          await refetchJobApplications();
          // Update currentCandidate with fresh data after refetch
          if (currentCandidate?.jobApplication?.id) {
            const updatedData = await AdminService.getCompanyJobApplications({
              pageNumber: 1,
              pageSize: 1,
              companyId,
              Filters: [
                `jobApplication.id:eq:${currentCandidate.jobApplication.id}`,
              ],
            });
            if (updatedData?.data?.[0]) {
              setCurrentCandidate(updatedData.data[0]);
            }
          }
        }}
      />
    </div>
  );
}
